import axios, { AxiosResponse, CancelToken, CancelTokenSource } from "axios"
import { ExcluirFlujosTesoreriaDeConciliacionCommandProps } from "./Commands/ExcluirFlujosTesoreriaDeConciliacionCommand"
import { VincularFacturaAFlujosTesoreriaCommandProps } from "./Commands/VincularFacturaAFlujosTesoreriaCommand"
import { FlujoTesoreriaReadingDtoProps } from "./DTOs/FlujoTesoreriaReadingDto"
import { GetFlujoDeTesoreriaDisponibleByFacturaQueryDto } from "./DTOs/GetFlujoDeTesoreriaDisponibleByFacturaQueryDto"
import {
  flujoTesoreriaReadingFiltersDtoPropsAdapter,
  pagosCobrosDisponiblesAdapter,
} from "./FlujoTesoreria.adapter"
import { flujosTesoreriaById } from "./FlujoTesoreriaById.adapter"
import { FlujoTesoreriaReadingFiltersDtoIPagedEnumerableProps } from "./Schemas/FlujoTesoreriaReadingFiltersDtoIPagedEnumerable"
import { GetFlujoDeTesoreriaDisponibleByFacturaQueryDtoIPagedEnumerable } from "./Schemas/GetFlujoDeTesoreriaDisponibleByFacturaQueryDtoIPagedEnumerable"
import { GetFlujosConciliablesDtoIPagedEnumerableProps } from "./Schemas/GetFlujosConciliablesDtoIPagedEnumerable"
import { entidadConciliableReadingDtoAdapter } from "./adapter_globals/EntidadConciliableReadingDto.adapter"
import { ConciliablesQueryParamsProps } from "./interfaces/ConciliablesQueryParams"
import { SortingCriteria } from "./interfaces/SortingCriteria"

export interface QueryParamsProps {
  FechaLowerLimit?: string
  FechaUpperLimit?: string
  Keyword?: string
  SortingCriteria?: string
  MaxResultCount?: number
  SkipCount?: number
}

export async function getPagosCobrosDisponiblesFacturaId(
  facturaId: string,
  queryParams?: QueryParamsProps
): Promise<GetFlujoDeTesoreriaDisponibleByFacturaQueryDtoIPagedEnumerable> {
  const {
    data,
  }: { data: GetFlujoDeTesoreriaDisponibleByFacturaQueryDtoIPagedEnumerable } =
    await axios.get(`/FlujoTesoreria/disponibles/factura/${facturaId}`, {
      params: queryParams,
    })
  return {
    currentPage: data.currentPage,
    items: data.items.map(
      (pagoCobro: GetFlujoDeTesoreriaDisponibleByFacturaQueryDto) =>
        pagosCobrosDisponiblesAdapter(pagoCobro)
    ),
    totalCount: data.totalCount,
  }
}

export async function putPagosCobrosAddFactura(
  queryParams: VincularFacturaAFlujosTesoreriaCommandProps
) {
  const { facturaId, ...rest } = queryParams
  const data = await axios.put(`/FlujoTesoreria/factura/${facturaId}`, rest)
  return data
}

export async function getPagosCobrosById(id: string) {
  const { data }: { data: FlujoTesoreriaReadingDtoProps } = await axios.get(
    `/FlujoTesoreria/${id}`
  )
  return flujosTesoreriaById(data)
}

export interface getFlujoTesoreriaReadingFiltersDtoIPagedEnumerableParamsProps {
  FechaLowerLimit?: string
  FechaUpperLimit?: string
  ImporteLowerLimit?: number
  ImporteUpperLimit?: number
  Keyword?: string
  SortingCriteria?: SortingCriteria
  IsEnabled?: boolean
  MaxResultCount?: number
  SkipCount?: number
}

export async function getFlujoTesoreriaReadingFiltersDtoIPagedEnumerable(
  queryParams: getFlujoTesoreriaReadingFiltersDtoIPagedEnumerableParamsProps,
  cancelToken?: CancelToken
): Promise<FlujoTesoreriaReadingFiltersDtoIPagedEnumerableProps> {
  const {
    data,
  }: { data: FlujoTesoreriaReadingFiltersDtoIPagedEnumerableProps } =
    await axios.get(`/FlujoTesoreria/by-filters`, {
      params: queryParams,
      cancelToken,
    })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((flujoTesoreria) =>
      flujoTesoreriaReadingFiltersDtoPropsAdapter(flujoTesoreria)
    ),
    totalCount: data.totalCount,
  }
}

export async function getFlujoTesoreriaTotalImporte(
  queryParams: getFlujoTesoreriaReadingFiltersDtoIPagedEnumerableParamsProps,
  cancelToken?: CancelToken
): Promise<number> {
  const { data } = await axios.get(`/FlujoTesoreria/total-importe`, {
    params: queryParams,
    cancelToken,
  })
  return data
}

// eslint-disable-next-line import/no-mutable-exports
export let flujoTesoreriaConciliablesCall: CancelTokenSource
export async function getFlujoTesoreriaConciliables(
  queryParams: ConciliablesQueryParamsProps
): Promise<GetFlujosConciliablesDtoIPagedEnumerableProps> {
  if (typeof flujoTesoreriaConciliablesCall !== typeof undefined) {
    flujoTesoreriaConciliablesCall.cancel("Previous Request Cancelled")
  }
  flujoTesoreriaConciliablesCall = axios.CancelToken.source()
  const {
    data,
  }: Record<"data", GetFlujosConciliablesDtoIPagedEnumerableProps> =
    await axios.get("/FlujoTesoreria/conciliables", {
      params: queryParams,
      cancelToken: flujoTesoreriaConciliablesCall.token,
    })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((flujoTesoreria) =>
      entidadConciliableReadingDtoAdapter(flujoTesoreria)
    ),
    totalCount: data.totalCount,
  }
}

/**
 * Deletes a FlujoTesoreria record by its ID.
 * @param id - The ID of the FlujoTesoreria record to delete.
 * @returns A Promise that resolves to the AxiosResponse object.
 */
export async function deleteFlujoTesoreriaById(
  id: string
): Promise<AxiosResponse> {
  const response = axios.delete(`/FlujoTesoreria/${id}`)
  return response
}

export async function deleteFlujoTesoreriaBulk(
  requestBody: string[]
): Promise<AxiosResponse> {
  const response = axios.delete(`/FlujoTesoreria/bulk`, { data: requestBody })
  return response
}

export async function getFlujoTesoreriaById(
  id: string
): Promise<FlujoTesoreriaReadingDtoProps> {
  const { data } = await axios.get(`/FlujoTesoreria/${id}`)
  return data
}

export async function getFlujoTesoreriaConciliablesByAsientoContableId(
  asientoContableId: string,
  queryParams?: ConciliablesQueryParamsProps
): Promise<GetFlujosConciliablesDtoIPagedEnumerableProps> {
  const {
    data,
  }: Record<"data", GetFlujosConciliablesDtoIPagedEnumerableProps> =
    await axios.get(
      `/FlujoTesoreria/conciliables/asientoContable/${asientoContableId}`,
      {
        params: queryParams,
      }
    )
  return {
    currentPage: data.currentPage,
    items: data.items?.map((flujoTesoreria) =>
      entidadConciliableReadingDtoAdapter(flujoTesoreria)
    ),
    totalCount: data.totalCount,
  }
}

export async function postFlujoTesoreriaExcluirConciliacion(
  body: ExcluirFlujosTesoreriaDeConciliacionCommandProps
): Promise<AxiosResponse> {
  const response = axios.post("/FlujoTesoreria/excluir-conciliacion", body)
  return response
}
interface ForzarRestaurarConciliacionRequestBodyProps {
  flujoTesoreriaIds: string[]
}

export async function putFlujoTesoreriaRestaurarConciliacionForzada(
  requestBody: ForzarRestaurarConciliacionRequestBodyProps
): Promise<AxiosResponse> {
  const response = await axios.put(
    `/FlujoTesoreria/restaurar-conciliacion-forzada`,
    requestBody
  )
  return response
}
export async function putFlujoTesoreriaForzarConciliacion(
  requestBody: ForzarRestaurarConciliacionRequestBodyProps
): Promise<AxiosResponse> {
  const response = await axios.put(
    `/FlujoTesoreria/forzar-conciliacion`,
    requestBody
  )
  return response
}
